import { TIMEZONE_TO_COUNTRY_CODE } from '@features/egifts/constants/ConfigContext'

export function b64Encode(str: string) {
  return btoa(str)
}

/**
 * Gets market code (CA, GB, US) from browser timezone.
 * Uses TIMEZONE_TO_COUNTRY_CODE mapping and converts country codes to market codes.
 * Falls back to 'US' if timezone doesn't map to supported markets.
 *
 * @returns Market code ('CA', 'GB', or 'US')
 */
export function getMarketFromTimezone(): string {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const countryCode = TIMEZONE_TO_COUNTRY_CODE[timezone]

  // Return country code if it's CA or GB
  if (countryCode === 'CA' || countryCode === 'GB') {
    return countryCode
  }

  // Default to US for all other country codes or if timezone not found
  return 'US'
}

export const isMaintenanceTimeInEST = () => {
  const maintenanceStartTime = new Date('2024-02-13T16:00:00.000-00:00')
  const maintenanceEndTime = new Date('2024-02-13T19:00:00.000-00:00')
  const currentTime = new Date()

  return currentTime >= maintenanceStartTime && currentTime <= maintenanceEndTime
}

export const createCurrencyFormatter = (currency: string) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
    currency,
  }).format

export const create2DecimalsCurrencyFormatter = (currency: string) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency,
  }).format

/**
 * Maps user market to currency code.
 *
 * @param market - User market code (e.g., 'CA', 'GB', 'US', or null)
 * @returns Currency code in lowercase ('usd', 'cad', 'gbp')
 */
export function getCurrencyFromMarket(market: string | null): string {
  if (market === 'CA') {
    return 'cad'
  }
  if (market === 'GB') {
    return 'gbp'
  }
  // Default to USD for US or any other market
  return 'usd'
}

/**
 * Formats a price in cents to a currency string.
 *
 * @param price - Price in cents
 * @param currency - Currency code in lowercase ('usd', 'cad', 'gbp')
 * @returns Formatted price string (e.g., "$20.99", "CA$24.99", "£14.99")
 */
export function formatPriceWithCurrency(price: number, currency: string): string {
  const currencyLower = currency.toLowerCase()
  const amount = price / 100 // Convert from cents to dollars

  // Custom currency symbols
  if (currencyLower === 'cad') {
    return `CA$${amount.toFixed(2)}`
  }
  if (currencyLower === 'gbp') {
    return `£${amount.toFixed(2)}`
  }
  // Default to USD
  return `$${amount.toFixed(2)}`
}

export function isDeviceIOS() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  )
}

export function isDeviceAndroid() {
  return navigator.userAgent.includes('Android')
}

function isHeicOrHeifFile(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type === 'image/heic' || type === 'image/heif') {
    return true
  }
  return /\.(heic|heif)$/i.test(file.name)
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const heic2anyModule = await import('heic2any')
  const heic2any = heic2anyModule.default

  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9,
  })

  const blob = Array.isArray(converted) ? converted[0] : converted
  if (!(blob instanceof Blob)) {
    throw new Error('Failed to convert HEIC image')
  }

  const outputName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
  return new File([blob], outputName, { type: 'image/jpeg' })
}

export const convertFileToBase64 = async (file: File): Promise<string> => {
  const fileToRead = isHeicOrHeifFile(file) ? await convertHeicToJpeg(file) : file

  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(fileToRead)

    fileReader.onload = () => {
      if (!fileReader.result) {
        reject('FileReader result is empty')
        return
      }
      resolve(fileReader.result?.toString())
    }

    fileReader.onerror = (error) => {
      reject(error)
    }
  })
}

export const uniqueId = () =>
  Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2)
